import { useState, useEffect } from "react";
import { RefreshCw } from "lucide-react";

export default function UpdatePrompt() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [swVersion, setSwVersion] = useState(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    // Only a service worker that replaces the one already controlling this page is an "update".
    // A fresh install (first visit, or the browser evicted the old worker, e.g. iOS) also fires
    // SW_UPDATED, which made the banner show on every app open.
    const hadController = !!navigator.serviceWorker.controller;
    const onMessage = (event) => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        const v = event.data.version;
        if (!hadController) { localStorage.setItem('v2r_seen_sw_version', String(v)); return; }
        // Don't nag if the user already saw / dismissed this exact version.
        if (localStorage.getItem('v2r_seen_sw_version') === String(v)) return;
        console.log('[UpdatePrompt] New version available:', v);
        setSwVersion(v);
        setShowUpdate(true);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);

    // NOTE: We intentionally do NOT auto-reload on 'controllerchange'.
    // Auto-reloading yanked users out of the login flow and killed in-progress
    // autosaves whenever a new service worker took control. The banner below lets
    // the user reload when it's safe.
  }, []);

  const handleRefresh = () => {
    if (swVersion != null) localStorage.setItem('v2r_seen_sw_version', String(swVersion));
    window.location.reload();
  };

  const handleDismiss = () => {
    if (swVersion != null) localStorage.setItem('v2r_seen_sw_version', String(swVersion));
    setShowUpdate(false);
  };

  if (!showUpdate) return null;

  return (
    <div 
      data-testid="update-prompt-banner"
      className="fixed top-0 left-0 right-0 z-[9999] bg-blue-600 text-white px-4 py-3 shadow-lg"
    >
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <RefreshCw className="h-5 w-5 animate-spin" />
          <span className="text-sm font-medium">
            A new version is available! {swVersion ? `(v${swVersion})` : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="update-refresh-btn"
            onClick={handleRefresh}
            className="bg-white text-blue-600 px-3 py-1 rounded text-sm font-medium hover:bg-blue-50 transition-colors"
          >
            Refresh Now
          </button>
          <button
            data-testid="update-dismiss-btn"
            onClick={handleDismiss}
            className="text-white/80 hover:text-white px-2 py-1 text-sm"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
